import {Log, User, UserManager, WebStorageStateStore} from "oidc-client-ts"
import MultiWindowUserManager from "./MultiWindowUserManager"
import axios from "./use-js-axios-instead-of-ts-axios.js"
import {createApp} from "vue"
import {jwtDecode} from "jwt-decode";
// @ts-ignore
import AuthScreen from "./AuthScreen.vue"
import {i18n, setupVuetify} from "../app/AppSetup.js"

Log.setLogger(console)

const QUERY_CUSTOMER_PARAM = 'c'
const NCS_CLIENT_ID = 'ncs-web'
const NCS_OIDC_AUTHORITY_KEY = "ncs.authority"
const NCS_OIDC_SAVED_CUSTOMER_KEY = "ncs.auth.customer"

interface AuthProvider {
    authority: string
    displayName: string
    isOpsRealm: boolean
}

interface AuthConfig {
    multiTenantMode: boolean
    customer: string
    canonicalHost: string
    provider: Array<AuthProvider>
}

class AuthManager {

    private userManager: UserManager | null = null;
    private savedAuthorities: Array<AuthProvider> = [];
    private authError: string = null;
    private sessionRefreshUntil: number = 0;
    private sessionIdleTime: number = 0;
    private sessionKeepalive: boolean = false;
    private loggingOut: boolean = false;
    private sessionIdleCallback: () => void | null = null;
    private sessionResumeCallback: () => void | null = null;
    private sessionLogoutCallback: () => void | null = null;

    private onUserLogin(user: User) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.access_token;
        axios.defaults.withCredentials = true;

        // TODO: Error handling would be good
        let refreshToken = jwtDecode(user.refresh_token);
        authManager.sessionIdleTime = (refreshToken.exp - refreshToken.iat) * 1000;
        authManager.loggingOut = false;

        if (authManager.sessionResumeCallback != null) {
            authManager.sessionResumeCallback();
        }
    }

    private onUserLogout() {
        if (!authManager.loggingOut && authManager.sessionLogoutCallback) {
            authManager.sessionLogoutCallback();
        }
    }

    private onAccessTokenExpiring() {
        if (Date.now() > authManager.sessionRefreshUntil && !authManager.sessionKeepalive) {
            authManager.userManager.stopSilentRenew();
        }
    }

    private async onAccessTokenExpired() {
        if (authManager.sessionIdleCallback) {
            authManager.sessionIdleCallback();
        }
        await authManager.userManager.signoutSilent()
    }

    private getUserManager(authority: string) {
        if (this.userManager && this.userManager.settings.authority === authority) {
            return this.userManager;
        }

        // Note: We opt out of automatic silent renew at UserManager creation because it registers an accessTokenExpiring
        // event handler. We need to register our own handler before that, so we can stop the automatic refresh when the
        // user is idle.

        const userStore = new WebStorageStateStore({store: window.localStorage});

        const userManager = new MultiWindowUserManager({
            authority: authority,
            client_id: NCS_CLIENT_ID,
            redirect_uri: window.location.origin + window.location.pathname,
            post_logout_redirect_uri: window.location.origin + window.location.pathname,
            response_mode: "fragment",
            scope: 'openid profile email',
            automaticSilentRenew: false,
            userStore: userStore
        });

        // update axios when session is renewed
        userManager.events.addUserLoaded(this.onUserLogin);
        userManager.events.addUserUnloaded(this.onUserLogout);

        // if the token expires, send user to login screen
        userManager.events.addAccessTokenExpired(this.onAccessTokenExpired);

        // Register our AccessTokenExpiring handler, then enable automatic renewal
        userManager.events.addAccessTokenExpiring(this.onAccessTokenExpiring);
        userManager.startSilentRenew();

        /**
         * We could register events here, if they are ever useful.
         *
         * Examples: addAccessTokenExpired, addAccessTokenExpiring, addSilentRenewError, addUserLoaded, addUserUnloaded,addUserSignedOut, addUserSessionChanged
         *
         */
        // userManager.events.addAccessTokenExpiring(function ()
        // {
        //     console.log("token expiring...")
        // })

        this.userManager = userManager;
        return userManager;
    }

    private customerFromQuery() {
        const customerRegex = '[&?]' + QUERY_CUSTOMER_PARAM + '=([^&]+)';
        let searchMatches = window.location.search.match(customerRegex)
        if (searchMatches) {
            return searchMatches[1]
        }

        let hashMatches = window.location.hash.match(customerRegex)
        if (hashMatches) {
            return hashMatches[1]
        }

        return null
    }

    private isOpsRedirect() {
        const customerRegex = '[&?]reauth=ops[&$]';
        let searchMatches = window.location.search.match(customerRegex)
        if (searchMatches) {
            return true
        }

        let hashMatches = window.location.hash.match(customerRegex)
        return !!hashMatches;
    }

    private readonly AUTH_URL = '/web-support/auth'

    private async directUserToLoginPage(usePopup: boolean) {
        let authority: string | null = null;
        let savedCustomer: string | null;
        let queryCustomer = this.customerFromQuery()

        // First, look up the current authority, and the list of possible authorities, in local storage
        try {
            authority = localStorage.getItem(NCS_OIDC_AUTHORITY_KEY)
            savedCustomer = localStorage.getItem(NCS_OIDC_SAVED_CUSTOMER_KEY)
        } catch (e) {
            console.error("Could not get authority from local storage")
        }

        // Query the server for possible authorities
        let authUrlSuffix = ""
        if (savedCustomer) {
            authUrlSuffix = '/' + savedCustomer
        }
        const authResponse = await axios.get<AuthConfig>(this.AUTH_URL + authUrlSuffix)
        const authData = authResponse.data

        // If an authority is saved, and that authority is in the list of possible authorities, the user is already
        // logged in or has just logged in, and we can complete the login flow.
        const matchedProvider = authData.provider.find(x => x.authority === authority);
        if (authority && matchedProvider) {
            // If there is an active authority, handle either an already-active session or the post-login auth callback
            const user = await this.handleUserLogin(authority)
            if (user != null) {
                // If we have a user, login was successful. Otherwise, we'll need to initiate login
                return user
            }
        }

        let targetCustomer = queryCustomer || authData.customer
        let needsRedirect = this.redirectToCanonicalHost(authData, targetCustomer);
        if (needsRedirect) {
            return
        }

        if (authData.multiTenantMode) {
            // In multi-tenant mode, we have one or more authorities that can be used for login. The "operations"
            // realm is always returned. If there are any other authorities, they will belong to a customer that was
            // inferred from the URL. There should always be either one or two authorities.
            console.log("[initLogin] Handling multi-tenant configuration", authData)

            // Take the authorities from the response instead of the saved ones.
            // The selector form will allow the user to switch customers if we inferred the wrong one.
            this.savedAuthorities = authData.provider
            if (usePopup && authority) {
                return this.continueLogin(matchedProvider, targetCustomer, usePopup)
            } else {
                this.presentLoginForm()
            }
        } else {
            // In single-tenant mode, there is only one authority. Skip the selector form and redirect directly to
            // that realm for login.
            console.log("[initLogin] Handling single-tenant configuration")

            let provider = authData.provider[0]
            if (this.isOpsRedirect()) {
                provider = authData.provider.find(x => x.isOpsRealm)
            }

            return this.continueLogin(provider, targetCustomer, usePopup)
        }
    }

    private redirectToCanonicalHost(authData: AuthConfig, targetCustomer: string) {
        // If this is a request to restart authentication as an ops user, redirect to the canonical host
        // There's an exception for localhost,
        if (this.isOpsRedirect() && window.location.hostname !== 'localhost' && authData.canonicalHost && authData.canonicalHost !== window.location.host) {
            let hash = window.location.hash || '#'
            if (targetCustomer) {
                if (hash.includes('?')) {
                    hash += '&'
                } else {
                    hash += '?'
                }
                hash += QUERY_CUSTOMER_PARAM + '=' + targetCustomer
            }
            let target = window.location.protocol + '//' + authData.canonicalHost + window.location.pathname + window.location.search + hash
            console.log("[initLogin] Redirecting to canonical host", target)
            window.location.href = target
            return true
        }
        return false
    }

    // This is the entrypoint into the login process.
    public async initLogin() {
        // Have axios call our heartbeat function on each request
        axios.interceptors.request.use(
            config => {
                console.log ("[AuthManger.initLogin Heartbeat called")
                this.heartbeat();
                return config;
            }
        );
        return await this.directUserToLoginPage(false);
    }

    public async loginPopup() {
        await this.directUserToLoginPage(true);
    }

    private async handleUserLogin(authority: string) {
        let userManager = this.getUserManager(authority)
        // First, check if there is already a logged-in session. If so, we can skip the remaining checks.
        const user = await userManager.getUser()
        if (user != null && !user.expired && user.profile.iss.replace(/\/$/, '') === authority.replace(/\/$/, '')) {
            return this.completeLogin(user)
        }

        // Next, check if the auth callback parameters are present
        const hash = window.location.hash
        // This regular expression matches both #state= and #/state= at the beginning of the string.
        if (/^#\/?state=/.test(hash)) {
            // If so, handle the auth callback. We need to adjust the URL slightly, because extra slashes cause problems
            // for the parameter parser.
            let url = hash
            if (url.startsWith('#/')) {
                url = '#' + url.slice(2)
            }
            if (url.endsWith('/')) {
                url = url.slice(0, -1)
            }
            url = new URL(url, window.location.href).href
            try {
                // Invoke the signin callback. If this succeeds, the user was successfully authenticated
                const user = await userManager.signinCallback(url)

                if (user instanceof User) {
                    return this.completeLogin(user)
                }
            } catch (ex) {
                console.log("Rejected! Not sure what to do now.", ex)
            }
        }
        // If the hash contains an error, let's clear the authority and redirect to the login page with an error message.
        else if (/^#\/?error=/.test(hash)) {
            console.log("[handleUserLogin] No valid callback parameters present", hash)
            this.authError = "auth.logInFailed"
            // Remove error hash, clear customer authority, and redirect to login page
            window.location.href = "/#/"
            this.forgetCustomerAuthority()
        }
    }

    private presentLoginForm() {
        //let vuetify = setupVuetify();
        //let i18n:Plugin = configureI18n();

        const loginApp = createApp (AuthScreen, {providers: this.savedAuthorities})
        loginApp.use (setupVuetify())
        //@ts-ignore
        loginApp.use (i18n)
        loginApp.mount('#app')
        /***
        new Vue({
            el: '#app',
            data: {providers: this.savedAuthorities},
            template: '<AuthScreen :providers="providers" />',
            components: {AuthScreen},
            vuetify,
            i18n
        })
         ***/
    }

    public async lookupCustomerAuthority(customer: string) {
        const authResponse = await axios.get<AuthConfig>(this.AUTH_URL + '/' + customer)
        const authData = authResponse.data
        localStorage.setItem(NCS_OIDC_SAVED_CUSTOMER_KEY, customer)
        this.savedAuthorities = authData.provider

        if (authData.multiTenantMode) {
            // In multi-tenant mode, we expect two realms to be returned: the customer's realm, and the operations'
            // realm. If there is only one, in this path, it indicates that the requested customer was not found
            console.log("[lookupCustomerAuthority] Handling multi-tenant configuration", authData)

            const customerProvider = authData.provider.find(x => !x.isOpsRealm)
            if (customerProvider) {
                return this.continueLogin(customerProvider, customer, false)
            }
        } else {
            // In single-tenant mode, there is only one authority. Skip the selector form and redirect directly to
            // that realm for login.
            console.log("[lookupCustomerAuthority] Handling single-tenant configuration")

            const provider = authData.provider.find(x => x.isOpsRealm)
            if (provider) {
                return this.continueLogin(provider, customer, false)
            }
        }

        console.log("[lookupCustomerAuthority] No customer provider found")
        this.authError = "auth.customerNotFound" // We probably want to be extremely vague here - potentially matching "Login Failed"
        return Promise.reject("auth.customerNotFound")
    }

    public forgetCustomerAuthority() {
        this.savedAuthorities = this.savedAuthorities.filter(x => x.isOpsRealm)
        localStorage.removeItem(NCS_OIDC_SAVED_CUSTOMER_KEY)
        return this.savedAuthorities
    }

    public async continueLogin(provider: AuthProvider, customer: string, usePopup: boolean = false) {
        try {
            localStorage.setItem(NCS_OIDC_AUTHORITY_KEY, provider.authority)
        } catch (e) {
            console.error("Could not save authority to local storage")
        }

        // Redirect to the identity provider for authentication.
        // Squirrel away the current information for the Vue router in the state object, so it can be restored
        // after the login completes.
        let userManager = this.getUserManager(provider.authority)
        await userManager.clearStaleState()

        this.authError = null

        const state = {
            route: this.stripAuthParams(window.location.hash),
            customer: customer
        };
        const url_state = customer ? QUERY_CUSTOMER_PARAM + "=" + customer : undefined

        if (usePopup) {
            return userManager.signinPopup({state, url_state})
        } else {
            return userManager.signinRedirect({state, url_state})
        }
    }

    private async completeLogin(user: User): Promise<User> {
        let sessionState = null
        if (user?.state) {
            sessionState = user.state
        }

        this.onUserLogin(user)

        // Restore route information from session state
        let route = window.location.hash || '#/'

        route = this.stripAuthParams(route);

        if (/^#\/?state=/.test(route)) {
            route = '#/'
        }
        // @ts-ignore
        if (sessionState?.route) {
            // @ts-ignore
            route = sessionState?.route
        }
        window.location.replace(new URL(route, window.location.href))

        return user
    }

    private stripAuthParams(route: string) {
        if (route.includes('?')) {
            let args = route.substring(route.indexOf('?') + 1).split('&')
            let newArgs = []
            for (let arg of args) {
                // Remove parameters that we don't specifically need to keep in the URL
                if (arg.startsWith('reauth=')) {
                    continue
                }
                if (arg.startsWith('state=')) {
                    continue
                }
                newArgs.push(arg)
            }
            route = route.substring(0, route.indexOf('?')) + '?' + newArgs.join('&')
        }
        return route;
    }

    public async logout() {
        if (this.userManager) {
            this.loggingOut = true;
            await this.userManager.signoutRedirect()
        }
    }

    public heartbeat(): void {
        this.sessionRefreshUntil = Date.now() + this.sessionIdleTime;
        if (this.userManager) {
            this.userManager.startSilentRenew();
        }
    }

    public setKeepAlive(value: boolean) {
        this.sessionKeepalive = value;
        if (value) {
            this.heartbeat();
        }
    }

    public addSessionIdleHandler(handler: (() => void)) {
        this.sessionIdleCallback = handler;
    }

    public addSessionResumeHandler(handler: (() => void)) {
        this.sessionResumeCallback = handler;
    }

    public addSessionLogoutHandler(handler: (() => void)) {
        this.sessionLogoutCallback = handler;
    }
}

const authManager = new AuthManager()
export default authManager
export {AuthProvider}
